import type { ClientMessage, ServerMessage } from '@sf72/shared';
import { getInitData } from '../telegram.js';

/**
 * WebSocket client.
 *
 * Responsibilities kept here so the UI never deals with the wire: it
 * authenticates on open, queues anything sent before the socket is ready, and
 * reconnects with backoff. The server gives a disconnected player a grace
 * period before forfeiting, so getting back quickly actually matters.
 */

export type ConnectionStatus = 'connecting' | 'authenticating' | 'ready' | 'offline';

export interface SocketHandlers {
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: ConnectionStatus) => void;
}

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 10_000;
const PING_INTERVAL_MS = 25_000;

function socketUrl(): string {
  const override = import.meta.env.VITE_WS_URL as string | undefined;
  if (override) return override;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

export class GameSocket {
  private socket: WebSocket | null = null;
  private queue: ClientMessage[] = [];
  private attempts = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private closedByUs = false;
  /** Re-sent after a reconnect so the player lands back in their match. */
  private joinedMatchId: string | null = null;

  constructor(private readonly handlers: SocketHandlers) {}

  connect(): void {
    this.closedByUs = false;
    this.clearReconnect();
    this.handlers.onStatus('connecting');

    const socket = new WebSocket(socketUrl());
    this.socket = socket;

    socket.onopen = () => {
      this.attempts = 0;
      this.handlers.onStatus('authenticating');
      this.sendNow({ t: 'auth', initData: getInitData() });
      this.startPing();
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }

      if (message.t === 'auth_ok') {
        this.handlers.onStatus('ready');
        this.flush();
        // A reconnect has to re-enter the room; the server does not remember
        // which match a socket was watching.
        if (this.joinedMatchId) {
          this.sendNow({ t: 'join', matchId: this.joinedMatchId });
        }
      }

      this.handlers.onMessage(message);
    };

    socket.onclose = () => {
      this.stopPing();
      this.socket = null;
      if (this.closedByUs) return;
      this.handlers.onStatus('offline');
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // `onclose` always follows, and that is where reconnection is handled.
    };
  }

  disconnect(): void {
    this.closedByUs = true;
    this.clearReconnect();
    this.stopPing();
    this.socket?.close();
    this.socket = null;
  }

  send(message: ClientMessage): void {
    if (message.t === 'join') this.joinedMatchId = message.matchId;
    if (message.t === 'leave') this.joinedMatchId = null;

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.sendNow(message);
    } else {
      this.queue.push(message);
    }
  }

  private sendNow(message: ClientMessage): void {
    // A socket that is closing still exists but throws on send, and the ping
    // timer fires on a schedule of its own — it has no way of knowing the
    // connection went away a moment ago.
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  private flush(): void {
    const pending = this.queue;
    this.queue = [];
    for (const message of pending) this.sendNow(message);
  }

  private scheduleReconnect(): void {
    this.attempts++;
    // Exponential backoff with jitter, so two clients dropped by the same
    // network blip do not retry in lockstep.
    const base = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (this.attempts - 1));
    const delay = base / 2 + Math.random() * (base / 2);
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.sendNow({ t: 'ping', ts: Date.now() });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}
